"use client";

import { ChevronDownIcon } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useState } from "react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { SidebarMenuItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { CreateViewFromPresetDialog } from "../../dialog/create-view-from-preset-dialog";
import { ProjectMoreMenu } from "./project-more-menu";
import SidebarMenuButtonWithPath from "./sidebar-menu-button-with-path";
import { useSidebarSearch } from "./sidebar-search-context";

type Props = {
  projectId: string;
  projectName: string;
  projectColor: string;
  projectType: "ua" | "sa" | "iw" | "ads";
  workspaceId: string;
  isProjectActive: boolean;
  linkHref: string;
  activePattern: string;
  children: ReactNode;
  userAnalyticsId?: string; // UA projects only
  lang?: string; // ADS projects only
  additionalMenuItems?: ReactNode;
  isDragging?: boolean; // ドラッグ中かどうか
};

export default function ProjectSidebarItem({
  projectId,
  projectName,
  projectColor,
  projectType,
  workspaceId,
  isProjectActive,
  linkHref,
  activePattern,
  children,
  userAnalyticsId,
  lang,
  additionalMenuItems,
  isDragging = false,
}: Props) {
  const { isFiltering } = useSidebarSearch();
  const [isOpen, setIsOpen] = useState(isProjectActive);
  const [showCreateViewDialog, setShowCreateViewDialog] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  useEffect(() => {
    if (isProjectActive) {
      setIsOpen(true);
    }
  }, [isProjectActive]);

  return (
    <Collapsible
      open={isFiltering ? true : isOpen}
      onOpenChange={isDragging || isFiltering ? undefined : setIsOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem className="sticky top-[calc(var(--sidebar-sticky-top,0px)+var(--sidebar-sticky-level-1))] z-10 bg-sidebar">
        <SidebarMenuButtonWithPath activePattern={activePattern} asChild>
          <Link
            href={linkHref}
            prefetch={false}
            className="flex h-7 items-center gap-1 pr-9 group/menu-button focus:ring-2 ring-inset"
            style={{
              // ドラッグ中はクリック不可にする
              pointerEvents: isDragging ? "none" : "auto",
              width: "calc(100% + 8px)",
            }}
            onDragStartCapture={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              // シェブロンアイコン部分のクリックを検出
              const target = e.target as HTMLElement;
              const clickedOnIcon = target.closest("[data-chevron-icon]");

              if (clickedOnIcon) {
                // シェブロンクリック時は遷移をキャンセル
                e.preventDefault();
                if (!isFiltering) {
                  // Collapsibleのトグル
                  setIsOpen((isOpen) => !isOpen);
                }
              } else if (!isFiltering) {
                // プロジェクト名クリック時は遷移+オープン
                setIsOpen(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (!isFiltering) {
                  setIsOpen((isOpen) => !isOpen);
                }
              }
            }}
          >
            <div
              role="button"
              tabIndex={-1}
              data-chevron-icon
              data-prevent-progress="true"
              className="relative size-4 shrink-0 rounded-sm hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors duration-75 cursor-pointer"
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
            >
              <div
                className={cn(
                  "size-3 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 rounded-sm transition-opacity duration-75 group-hover/menu-button:opacity-0 pointer-events-none",
                  projectColor === "#000000" && "bg-black dark:bg-white",
                )}
                style={{
                  backgroundColor:
                    projectColor === "#000000" ? "" : projectColor,
                }}
              />
              <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3! transition-all duration-75 opacity-0 group-hover/menu-button:opacity-100 group-data-[state=closed]/collapsible:-rotate-90 pointer-events-none" />
            </div>
            <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              <div className="line-clamp-1 min-w-0" title={projectName}>
                {projectName}
              </div>
            </span>
          </Link>
        </SidebarMenuButtonWithPath>
        <div
          className={cn(
            "pointer-events-none absolute top-0 right-0 z-20 flex h-7 items-center opacity-0 transition-opacity group-hover/menu-item:pointer-events-auto group-hover/menu-item:opacity-100",
            isMoreMenuOpen && "pointer-events-auto opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ProjectMoreMenu
            projectId={projectId}
            projectType={projectType}
            workspaceId={workspaceId}
            projectName={projectName}
            lang={lang}
            open={isMoreMenuOpen}
            onOpenChange={setIsMoreMenuOpen}
            className="bg-sidebar shadow-sm"
            onCreateViewFromPreset={
              userAnalyticsId ? () => setShowCreateViewDialog(true) : undefined
            }
            additionalMenuItems={additionalMenuItems}
          />
        </div>
      </SidebarMenuItem>
      <CollapsibleContent>
        <div
          onPointerDown={(e) => {
            // ビュー一覧部分でのドラッグ開始を防止
            e.stopPropagation();
          }}
        >
          {children}
        </div>
      </CollapsibleContent>

      {/* CreateViewFromPresetDialog */}
      {userAnalyticsId && (
        <CreateViewFromPresetDialog
          workspaceId={workspaceId}
          projectId={projectId}
          userAnalyticsId={userAnalyticsId}
          open={showCreateViewDialog}
          onOpenChange={setShowCreateViewDialog}
        />
      )}
    </Collapsible>
  );
}
